// Shared dialog focus primitives.
//
// Modal.js has owned this behaviour since it was written; CommandK's
// hand-rolled palette needs the identical algorithm (doc-258), so it lives
// here instead of being copied into a second component. Any NEW dialog that
// handles Escape on a document listener belongs on the stack below (doc-262).

// ───── dialog stack ─────
//
// Both Modal and CommandK listen for Escape on `document`, so when the palette
// is opened over a Modal (⌘K works from inside one) a single Escape used to
// close BOTH — neither listener knew the other existed (doc-262). This LIFO
// stack is the shared answer to "am I the dialog that owns the keyboard right
// now": each dialog registers when it opens and unregisters when it closes, and
// only the top entry acts on Escape.
const dialogStack = [];

// Drop entries whose element left the document. Registration is paired with the
// keydown listener's lifetime, so this should never find anything — it is a
// backstop so one missed unregister cannot wedge Escape for every dialog under
// it.
function pruneDetached() {
  for (let i = dialogStack.length - 1; i >= 0; i -= 1) {
    if (!dialogStack[i].isConnected) dialogStack.splice(i, 1);
  }
}

export function pushDialog(node) {
  if (!node) return;
  popDialog(node);
  dialogStack.push(node);
}

// Remove by identity, not by popping the tail: React 18 StrictMode
// double-invokes effects in dev and unmount order under a route change is not
// guaranteed, so a blind pop can desync the stack.
export function popDialog(node) {
  const index = dialogStack.lastIndexOf(node);
  if (index !== -1) dialogStack.splice(index, 1);
}

// A dialog that never registered counts as top, so a component which hasn't
// opted in keeps its old unconditional behaviour — a dead Escape key is a worse
// failure than an over-eager one.
export function isTopDialog(node) {
  if (!node) return true;
  pruneDetached();
  const index = dialogStack.lastIndexOf(node);
  if (index === -1) return true;
  return index === dialogStack.length - 1;
}

export const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Keep Tab / Shift+Tab inside `dialog`: wrap at the edges, and pull focus back
// in when it has already escaped (or when the keydown started outside).
// With a single focusable — the command palette, whose only tab stop is its
// input — first === last, so both branches fire and Tab is simply swallowed.
export function trapTabWithin(event, dialog) {
  if (!dialog) return;
  const focusable = dialog.querySelectorAll(FOCUSABLE_SELECTOR);
  if (!focusable.length) {
    event.preventDefault();
    dialog.focus();
    return;
  }
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;
  if (event.shiftKey && (active === first || !dialog.contains(active))) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
    event.preventDefault();
    first.focus();
  }
}
